import React, { ActionDispatch, FC, useContext, useEffect, useMemo } from "react";
import { ResultRegion } from "@app/projects/results/common";
import {
  DragAndDropMultiFileUploaderContext,
  DragAndDropMultiFileUploaderContextProvider
} from "@app/shared/DragAndDropMultiFileUploaderContext";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import ExceptionsService from "@app/exceptions/exceptionsService";
import { authenticatedDownload } from "@app/shared/AuthenticatedDownload";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { some, uniqueId } from "lodash";
import moment from "moment/moment";
import cx from "classnames";
import {
  EditExceptionForm,
  EditExceptionFormAction,
  EditExceptionFormActionType
} from "@app/exceptions/editExceptionReducer";
import { SpecialtyGroupException, SupplementaryDocument } from "@app/exceptions/interfaces";
import {
  SpecialtyGroupExceptionGrade, SpecialtyGroupExceptionReviewStatus,
  SpecialtyGroupExceptionReviewStatusLabels, SpecialtyGroupExceptionStatus
} from "@app/exceptions/enums";
import DragAndDropMultiFileUploader from "@app/shared/DragAndDropMultiFileUploader";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { sortStringListOrderAscending } from "@app/shared/utility/sortStringListOrderAscending";
import { UserContext } from "@app/user/UserContext";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextArea } from "@app/shared/qes-standards/forms/TextArea";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";
import { useQuery } from "@tanstack/react-query";

interface SpecialtyGroupExceptionPanelProps {
  customProjectId: number;
  exception: SpecialtyGroupException;
  expandedColumnSizes?: boolean;
  formState: EditExceptionForm;
  region: ResultRegion;
  formDispatch: ActionDispatch<[action: EditExceptionFormAction]>;
  specialtyGroupCode: string;
  specialtyGroupName: string;
}

export const getInitialFormState = (exception: SpecialtyGroupException): EditExceptionForm => {
  const unarchivedExceptionToLoad = exception?.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.Archived ? undefined : exception;
  const defaultStatus = !unarchivedExceptionToLoad || unarchivedExceptionToLoad && (unarchivedExceptionToLoad.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.None || unarchivedExceptionToLoad.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.Archived)
    ? SpecialtyGroupExceptionStatus.Pending
    : unarchivedExceptionToLoad.specialtyGroupExceptionStatus;
  const startDate = unarchivedExceptionToLoad && unarchivedExceptionToLoad.startDate ? moment(unarchivedExceptionToLoad.startDate).format("YYYY-MM-DD") : '---';
  const endDate = unarchivedExceptionToLoad && unarchivedExceptionToLoad.endDate ? moment(unarchivedExceptionToLoad.endDate).format("YYYY-MM-DD") : '---';
  const originalStartDate = startDate !== '---' ? startDate : '';
  const originalEndDate = endDate !== '---' ? endDate : '';
  return {
    comment: unarchivedExceptionToLoad && unarchivedExceptionToLoad.comment || "",
    endDate: originalEndDate,
    excludeFromCCIIOJustification: unarchivedExceptionToLoad && unarchivedExceptionToLoad.excludeFromCCIIOJustification || false,
    grade: unarchivedExceptionToLoad && unarchivedExceptionToLoad.grade || SpecialtyGroupExceptionGrade.None,
    narrative: unarchivedExceptionToLoad && unarchivedExceptionToLoad.exceptionNarrative || "",
    originalComment: unarchivedExceptionToLoad && unarchivedExceptionToLoad.comment || "",
    originalEndDate: originalEndDate,
    originalExcludeFromCCIIOJustification: unarchivedExceptionToLoad && unarchivedExceptionToLoad.excludeFromCCIIOJustification || false,
    originalGrade: unarchivedExceptionToLoad && unarchivedExceptionToLoad.grade || SpecialtyGroupExceptionGrade.None,
    originalNarrative: unarchivedExceptionToLoad && unarchivedExceptionToLoad.exceptionNarrative || "",
    originalPreviouslyApproved: unarchivedExceptionToLoad && unarchivedExceptionToLoad.previouslyApproved || false,
    originalReviewStatus: unarchivedExceptionToLoad && unarchivedExceptionToLoad.reviewStatus || SpecialtyGroupExceptionReviewStatus.None,
    originalStartDate: originalStartDate,
    originalStatus: defaultStatus,
    originalUploadedDocuments: unarchivedExceptionToLoad && unarchivedExceptionToLoad.supplementaryDocuments || [],
    previouslyApproved: unarchivedExceptionToLoad && unarchivedExceptionToLoad.previouslyApproved || false,
    reviewStatus: unarchivedExceptionToLoad && unarchivedExceptionToLoad.reviewStatus || SpecialtyGroupExceptionReviewStatus.None,
    startDate: originalStartDate,
    status: defaultStatus,
    uploadedDocuments: !!exception ? exception.supplementaryDocuments : [],
    validEndDate: true,
    validStartDate: true
  }
}

export const mergeFormAndException = (exception: SpecialtyGroupException, state: EditExceptionForm, region: ResultRegion, specialtyGroupCode: string): SpecialtyGroupException => {
  const result: Partial<SpecialtyGroupException> = !!exception
    ? {
      ...exception,
      comment: state.comment,
      endDate: state.endDate ? moment(state.endDate).toDate() : undefined,
      exceptionNarrative: state.narrative,
      excludeFromCCIIOJustification: state.excludeFromCCIIOJustification,
      grade: state.grade,
      previouslyApproved: state.previouslyApproved,
      reviewStatus: state.reviewStatus,
      specialtyGroupExceptionStatus: state.status,
      startDate: state.startDate ? moment(state.startDate).toDate() : undefined,
      supplementaryDocuments: state.uploadedDocuments,
    }
    : {
      ...region,
      comment: state.comment,
      endDate: state.endDate ? moment(state.endDate).toDate() : undefined,
      exceptionNarrative: state.narrative,
      excludeFromCCIIOJustification: state.excludeFromCCIIOJustification,
      grade: state.grade,
      previouslyApproved: state.previouslyApproved,
      reviewStatus: state.reviewStatus,
      specialtyGroupCode: specialtyGroupCode,
      specialtyGroupExceptionStatus: state.status,
      startDate: state.startDate ? moment(state.startDate).toDate() : undefined,
      supplementaryDocuments: state.uploadedDocuments,
    };
  return result as SpecialtyGroupException;
}

const WrappedSpecialtyGroupExceptionPanel: FC<React.PropsWithChildren<SpecialtyGroupExceptionPanelProps>> = ({
  customProjectId: customProjectIdProp,
  exception,
  expandedColumnSizes,
  specialtyGroupName,
  region,
  formState,
  formDispatch,
}: SpecialtyGroupExceptionPanelProps) => {
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const multiFileUploaderContext = useContext(DragAndDropMultiFileUploaderContext);
  const userContext = useContext(UserContext);
  const customProjectId = !!exception ? exception.customProjectId : customProjectIdProp;
  const uploadDocumentsApiKey: string = "document";
  const exceptionsService = useMemo(() => new ExceptionsService(), []);

  const { data: isCCIIO } = useQuery({
    enabled: !!customProjectId && featureFlags.has(Features.CCIIO_JUSTIFICATION_EXPORT),
    queryFn: ({ signal }) => exceptionsService.getCCIIOExceptionStatus([customProjectId], signal),
    queryKey: [ExceptionsService.getCCIIOExceptionStatusQueryKey, customProjectId],
    staleTime: 5 * 60 * 1000,
  })

  const statusId = uniqueId("specialty-group-exception-panel-status-");
  const commentsId = uniqueId("specialty-group-exception-panel-comments-");
  const exceptionNarrativeId = uniqueId("specialty-group-exception-panel-exception-narrative-");
  const gradeContainerClasses = "col-5 mt-3 pe-0";
  const disableGradeEdits = !userContext.roles.includes(Roles.SPECIALTY_EXCEPTION_REVIEWER_ADMIN);

  useEffect(() => {
    const initialState = getInitialFormState(exception);
    formDispatch({ payload: initialState, type: EditExceptionFormActionType.Reset });
    if (!exception) {
      multiFileUploaderContext.setUploadedFiles(new Map());
      return;
    }
    exception.supplementaryDocuments.forEach(item => {
      multiFileUploaderContext.setUploadedFiles(prevFiles => new Map(prevFiles).set(item.supplementaryDocumentMetadataId, new File([], item.documentName)));
    });
  }, [exception])

  const handleDownload = async (metadataId: number) => {
    const link = (!!metadataId && metadataId > 0) ?
      `${exceptionsService.downloadSupplementaryDocument(metadataId)}` : `#`;
    authenticatedDownload(link);
  };

  const handleFileUpload = async (file: File) => {

    return new Promise<void>(async (resolve, reject) => {
      try {
        const formData = new FormData();
        formData.append(uploadDocumentsApiKey, file);
        const metadataId = await exceptionsService.uploadSupplementaryDocument(formData);
        const fileinfo: FormDataEntryValue = formData.get(uploadDocumentsApiKey);
        if (!!metadataId && fileinfo instanceof File) {
          const supplementaryDocument: SupplementaryDocument = {
            documentName: fileinfo.name ? fileinfo.name : '',
            supplementaryDocumentMetadataId: metadataId
          }
          formDispatch({ payload: [supplementaryDocument], type: EditExceptionFormActionType.AddUploadedDocuments })
          multiFileUploaderContext.setUploadedFiles(prevFiles => new Map(prevFiles).set(metadataId, file));
          resolve();
        }
      } catch (error) {
        reject(error.responseJSON);
      }

    });
  };

  const shouldShowStatus = (value: SpecialtyGroupExceptionStatus) => {
    const statuses = [
      SpecialtyGroupExceptionStatus.Pending,
      SpecialtyGroupExceptionStatus.Submitted,
      SpecialtyGroupExceptionStatus.Approved,
      SpecialtyGroupExceptionStatus.Denied,
      SpecialtyGroupExceptionStatus.Appealed,
    ];
    return some(statuses, (s) => s === value);
  }

  return (
    <>
      <p>
        Please enter the current status of the Specialty Exception process
        for <strong>{specialtyGroupName}</strong> in <strong>{region.displayName}</strong> county...
      </p>

      <div className="row">
        <div className={cx({ "col-3": !expandedColumnSizes, "col-5": expandedColumnSizes, "mb-3": true })}>
          <QESSelect
            id={statusId}
            className="form-control"
            value={formState.status}
            onChange={e => formDispatch({ payload: e.target.value as SpecialtyGroupExceptionStatus, type: EditExceptionFormActionType.Status })}
            label="Status this year"
            testId="exception-panel__status-dropdown"
          >
            {Object.keys(SpecialtyGroupExceptionStatus).sort((a: string, b: string) => {
              return sortStringListOrderAscending(a, b);
            }).map((status) => {
              const value: SpecialtyGroupExceptionStatus = (SpecialtyGroupExceptionStatus)[status as keyof typeof SpecialtyGroupExceptionStatus];
              if (!shouldShowStatus(value)) {
                return null;
              }

              return (
                <option key={value} value={value}>{status}</option>
              );
            })}
          </QESSelect>
        </div>
      </div>

      <div>
        <QESDateInput
          containerClassName="mt-3 col-5 p-0"
          errorText="Start Date must be before Expiration Date"
          isValid={formState.validStartDate}
          label="Start Date"
          testId="exception-panel__start-date"
          onChange={(e) => formDispatch({ payload: e.target.value, type: EditExceptionFormActionType.StartDate })}
          unrequiredValidation={true}
          value={formState.startDate}
        />

        <QESDateInput
          containerClassName="mt-3 col-5 p-0"
          errorText="Expiration Date must be after Start Date"
          isValid={formState.validEndDate}
          label="Expiration Date"
          testId="exception-panel__end-date"
          onChange={(e) => formDispatch({ payload: e.target.value, type: EditExceptionFormActionType.EndDate })}
          unrequiredValidation={true}
          value={formState.endDate}
        />
      </div>

      <div className="row">
        <TooltipWrapper
          text={disableGradeEdits ? 'Users with sufficient permissions may edit the Exception Grade.' : null}
          containerClassName={disableGradeEdits ? gradeContainerClasses : 'w-100'}
          triggerWidth="100%">
          <div
            className={disableGradeEdits ? '' : gradeContainerClasses}>
            <QESSelect
              className="form-control"
              value={formState.grade}
              onChange={e => formDispatch({ payload: e.target.value as SpecialtyGroupExceptionGrade, type: EditExceptionFormActionType.Grade })}
              label="Exception Grade"
              testId="exception-panel__grade-dropdown"
              disabled={disableGradeEdits}
              validationTooltipContent={`A = Exception has been reviewed, has all necessary documentation attached and is ready for submission.
  B = Exception needs some work around the narrative and/or reviewing additional providers before submission.\n
  C = Exception is missing necessary supporting documentation, narrative, and/or excluded providers.`}
            >
              {Object.keys(SpecialtyGroupExceptionGrade).map((grade) => {
                const value: SpecialtyGroupExceptionGrade = (SpecialtyGroupExceptionGrade)[grade as keyof typeof SpecialtyGroupExceptionGrade];
                if (value === SpecialtyGroupExceptionGrade.None) {
                  return (
                    <option key={value} value={value}>---</option>
                  );
                }
                return (
                  <option key={value} value={value}>{grade}</option>
                );
              })}
            </QESSelect>
          </div>
        </TooltipWrapper>
      </div>

      <div>
        <QESSelect
          className="form-control"
          containerClassName="mt-3 mb-3 col-5 p-0"
          value={formState.reviewStatus}
          onChange={e => formDispatch({
            payload: e.target.value as SpecialtyGroupExceptionReviewStatus,
            type: EditExceptionFormActionType.ReviewStatus
          })}
          label="Internal Review Status"
          testId="exception-panel__review-status-dropdown"
        >
          {Object.values(SpecialtyGroupExceptionReviewStatus).map((reviewStatus) => (
            <option key={reviewStatus} value={reviewStatus}>
              {SpecialtyGroupExceptionReviewStatusLabels[reviewStatus]}
            </option>
          ))}
        </QESSelect>
      </div>

      <div className="row">
        <div className={cx({
          "col-12": true,
          "mb-3": !featureFlags.has(Features.CCIIO_JUSTIFICATION_EXPORT) || !isCCIIO
        })}>
          <div className="checkbox pt0-imp">
            <QESCheckbox label="Previously Approved" testId="exception-panel__previously-approved"
              checked={formState.previouslyApproved}
              onChange={e => formDispatch({ payload: e.target.checked, type: EditExceptionFormActionType.PreviouslyApproved })} />
          </div>
        </div>
      </div>

      {featureFlags.has(Features.CCIIO_JUSTIFICATION_EXPORT) && isCCIIO && (
        <div className="row">
          <div className="col-12 mb-3">
            <div className="checkbox pt0-imp">
              <QESCheckbox label="Select this box if exception(s) should not be included in the CCIIO Justification Form output" testId="exception-panel__exclude-from-cciio"
                           checked={formState.excludeFromCCIIOJustification}
                           onChange={e => formDispatch({ payload: e.target.checked, type: EditExceptionFormActionType.ExcludeFromCciioJustification })} />
            </div>
          </div>
        </div>
      )}

      <div className="row">
        <div className={cx({ "col-6": !expandedColumnSizes, "col-9": expandedColumnSizes })}>
          <QESTextArea
            label="Comments"
            maxLength={4000}
            rows={4}
            id={commentsId}
            value={formState.comment}
            onChange={e => formDispatch({ payload: e.target.value, type: EditExceptionFormActionType.Comment })}
            testId="exception-panel__comment"
          />
        </div>
      </div>

      <div className="row">
        <div className={cx({ "col-6": !expandedColumnSizes, "col-9": expandedColumnSizes })}>
          <QESTextArea
            label="Exception Narrative"
            maxLength={4000}
            rows={4}
            id={exceptionNarrativeId}
            value={formState.narrative}
            onChange={e => formDispatch({ payload: e.target.value, type: EditExceptionFormActionType.Narrative })}
            testId="exception-panel__exception-narrative"
          />
        </div>
      </div>

      <div className="row">
        <div className={cx({ "col-6": !expandedColumnSizes, "col-9": expandedColumnSizes })}>
          <RoleRestricted allowedRoles={[Roles.EXCEPTION_MANAGER]}>
            <DragAndDropMultiFileUploader
              apiKeyName={uploadDocumentsApiKey}
              title="Attach Supplemental Documents. Attachments must be in .csv, .doc, .docx, .png, .jpg, .pdf, .xls, .xlsx format."
              filetype=".csv,.doc,.docx,.png,.jpg,.jpeg,.pdf,.xls,.xlsx"
              onMultipleUpload={handleFileUpload}
              handleDownloadLink={handleDownload}
              onRemove={id => formDispatch({ payload: id, type: EditExceptionFormActionType.RemoveUploadedDocumentByKey })}
              small={true}
              testId="edit-project-upload-supplementary-docs"
            />
          </RoleRestricted>
        </div>
      </div>
    </>
  );
}

const SpecialtyGroupExceptionPanel: FC<React.PropsWithChildren<SpecialtyGroupExceptionPanelProps>> = (props: SpecialtyGroupExceptionPanelProps) => {
  return <DragAndDropMultiFileUploaderContextProvider>
    <WrappedSpecialtyGroupExceptionPanel {...props} />
  </DragAndDropMultiFileUploaderContextProvider>;
}

export default SpecialtyGroupExceptionPanel;